import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, of, from } from 'rxjs';
import {
    catchError,
    concatMap,
    debounce,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    withLatestFrom,
    mergeAll
} from 'rxjs/operators';
import * as fromCompanySettings from '../../company-setting';
import * as fromShared from '../../shared';
import { MessageService, ResponseStatus } from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { ToasterService } from '../../toaster';
import * as fromUser from '../../user-info/store';
import { QuickbooksService } from '../quickbooks.services';
import * as actions from './quickbooks.actions';
import * as qbSelectors from './quickbooks.selectors';
import moment from 'moment';
import {
    EntityActionFactory,
    EntityEffects,
    EntityOp,
    ofEntityOp,
    EntityAction,
    ofEntityType,
    EntityServices,
    EntityCollectionService
} from '@ngrx/data';
import * as fromDataServices from '../../data-services';

@Injectable()
export class QuickbooksEffects {
    SendInvoiceModelDS: EntityCollectionService<fromDataServices.Invoice.InvoiceModel>;
    constructor(
        private log: LoggingService,
        private actions$: Actions,
        private service: QuickbooksService,
        private store: Store,
        private toaster: ToasterService,
        private messageService: MessageService,
        public entityServices: EntityServices,
        public actionFactory: EntityActionFactory
    ) {
        this.SendInvoiceModelDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.InvoiceModel>(
                fromDataServices.Invoice.entityName
            );

        this.log.verbose({
            type: 'QuickbooksEffects::'
        });
    }
    notquickbooksOnline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromCompanySettings.upsertCompanySettings),
            filter(action =>
                action.companySettings.some(
                    setting =>
                        setting.SettingType === 'QuickbooksOnline' &&
                        (setting.SettingValue === '0' ||
                            setting.SettingValue === 'false' ||
                            setting.SettingValue === false ||
                            setting.SettingValue === 0)
                )
            ),
            tap(action =>
                this.log.verbose({
                    type: 'QuickbooksEffects:QuickbooksOnline$:not QBO',
                    action
                })
            ),
            mergeMap(() => [new actions.HasValidTokens(false)])
        );
    });
    quickbooksOnline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromCompanySettings.upsertCompanySettings),
            filter(action =>
                action.companySettings.some(
                    setting =>
                        setting.SettingType === 'QuickbooksOnline' &&
                        (setting.SettingValue === '1' ||
                            setting.SettingValue === 'true' ||
                            setting.SettingValue === true ||
                            setting.SettingValue === 1)
                )
            ),
            tap(action =>
                this.log.verbose({
                    type: 'QuickbooksEffects:QuickbooksOnline$:QBO',
                    action
                })
            ),
            switchMap(() =>
                this.service.HasValidTokens().pipe(
                    mergeMap(resp => [new actions.HasValidTokens(resp)]),
                    catchError(() => [new actions.HasValidTokens(false)]),
                    //not sure if this does anything
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    authUrl$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.GetAuthUrl>(actions.ActionTypes.GetAuthUrl),
            tap(a =>
                this.log.verbose({ type: 'QuickbooksEffects:authUrl$', a })
            ),
            switchMap(() =>
                this.service.GetAuthUrl().pipe(
                    map(resp => new actions.SetAuthUrl(resp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    quickbooksValidateTokens$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.ValidateTokens>(actions.ActionTypes.ValidateTokens),
            switchMap(() =>
                this.service.HasValidTokens().pipe(
                    map(resp => new actions.HasValidTokens(resp)),
                    catchError((e, c) => {
                        this.log.verbose({
                            type: 'QuickbooksEffects:quickbooksValidateTokens$',
                            e,
                            c
                        });
                        return [new actions.HasValidTokens(false)];
                    })
                )
            )
        );
    });
    private createUserPermissionsAction(
        categories: fromShared.Category[],
        hasValidTokens: boolean
    ): Action {
        return hasValidTokens
            ? new fromUser.SetUserPermissions({
                  UserProcessCategories: categories.map(category =>
                      category.CategoryId === 10
                          ? {
                                ...category,
                                UserProcesses: category.UserProcesses.filter(
                                    p => p.ProcessID > 0
                                )
                                    .map(p => ({
                                        ...p,
                                        disabled: false
                                    }))
                                    .concat([
                                        {
                                            ProcessID: -4,
                                            RouteName: 'qbrevoke',
                                            ProcessName:
                                                'Revoke QuickBooks Access',
                                            ProcessDesc:
                                                'Revoke QuickBooks Access',
                                            ProcessTitle:
                                                'Revoke QuickBooks Access',
                                            ProcessLabel:
                                                'Revoke eFAACT access to your QuickBooks account',
                                            disabled: false
                                        } as any
                                    ])
                            }
                          : category
                  )
              } as any)
            : new fromUser.SetUserPermissions({
                  UserProcessCategories: categories.map(category =>
                      category.CategoryId === 10
                          ? {
                                ...category,
                                UserProcesses: [
                                    {
                                        ProcessID: -5,
                                        RouteName: 'quickbooksauth',
                                        ProcessName: 'Connect to QuickBooks',
                                        ProcessTitle: 'Connect to QuickBooks',
                                        ProcessDesc: 'Connect to QuickBooks',
                                        ProcessLabel:
                                            'Connect your eFAACT account to QuickBooks',
                                        ProcessResourceImage:
                                            '/images/C2QB_green_btn_short_default.svg',
                                        ImageOnly: true
                                    },
                                    ...category.UserProcesses.filter(
                                        p => p.ProcessID > 0
                                    ).map(process => ({
                                        ...process,
                                        disabled:
                                            process.ProcessID === 8
                                                ? false
                                                : true
                                    }))
                                ]
                            }
                          : category
                  )
              } as any);
    }
    private latestQBInfo = () =>
        withLatestFrom(
            this.store.pipe(
                select(fromUser.getUserProcessCategories),
                filter(x => x.length > 0),
                tap(categories =>
                    this.log.verbose({
                        type: 'QuickbooksEffects:quickbooksUpdateQBOProcesses$:categories',
                        categories
                    })
                )
            ),
            this.store.pipe(
                select(qbSelectors.getHasValidTokens),
                tap(hasValidTokens =>
                    this.log.verbose({
                        type: 'QuickbooksEffects:quickbooksUpdateQBOProcesses$:hasValidTokens',
                        hasValidTokens
                    })
                )
            )
        );
    quickbooksUpdateQBOProcesses$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromUser.LoadUserPermissions>(
                fromUser.UserActionTypes.LoadUserPermissions
            ),
            tap(permissions =>
                this.log.verbose({
                    type: 'QuickbooksEffects:quickbooksUpdateQBOProcesses$:LoadUserPermissions',
                    permissions
                })
            ),
            switchMap(() =>
                // Wait for the next SetUserPermissions action
                this.actions$.pipe(
                    ofType<fromUser.SetUserPermissions>(
                        fromUser.UserActionTypes.SetUserPermissions
                    ),
                    take(1), // Only take the first SetUserInfo after LoadUserPermissions
                    this.latestQBInfo(),
                    tap(([userInfo, categories, hasValidTokens]) =>
                        this.log.verbose({
                            type: 'QuickbooksEffects:quickbooksUpdateQBOProcesses$',
                            userInfo,
                            hasValidTokens,
                            categories
                        })
                    ),
                    map(([userInfo, categories, hasValidTokens]) => {
                        return this.createUserPermissionsAction(
                            categories,
                            hasValidTokens
                        );
                    }),
                    tap(a =>
                        this.log.verbose({
                            type: 'QuickbooksEffects:quickbooksUpdateQBOProcesses$:categories mapped',
                            a
                        })
                    )
                )
            )
        );
    });
    hasValidTokens$ = createEffect(() => {
        return this.store.pipe(
            select(qbSelectors.getHasValidTokens),
            distinctUntilChanged(),
            tap(hasValidTokens =>
                this.log.verbose({
                    type: 'QuickbooksEffects:quickbooksUpdateQBOProcesses$:hasValidTokens',
                    hasValidTokens
                })
            ),
            this.latestQBInfo(),
            map(([userInfo, categories, hasValidTokens]) => {
                return this.createUserPermissionsAction(
                    categories,
                    hasValidTokens
                );
            })
        );
    });

    getEmployeesWithHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.GetEmployeesWithHours>(
                actions.ActionTypes.GetEmployeesWithHours
            ),
            filter(a => !!a.payload.StartDate && !!a.payload.EndDate),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:getEmployeesWithHours$',
                    a
                })
            ),
            switchMap(action =>
                this.service
                    .GetEmployeesWithHours(
                        action.payload.StartDate,
                        action.payload.EndDate,
                        action.payload.Resend
                    )
                    .pipe(
                        map(resp => new actions.SetEmployeesWithHours(resp)),
                        catchError((error, caught) => {
                            if (error.status === 424) {
                                return fromShared.Errors.handleServiceError(error, caught).pipe(
                                    concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                                );
                            }
                            return fromShared.Errors.handleServiceError(error, caught);
                        })
                    )
            )
        );
    });
    getMinApprovedTimeNotSent$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.GetMinApprovedTimeNotSent>(
                actions.ActionTypes.GetMinApprovedTimeNotSent
            ),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:GetMinApprovedTimeNotSent$',
                    a
                })
            ),
            switchMap(() =>
                this.service.GetMinApprovedTimeNotSent().pipe(
                    map(
                        resp =>
                            new actions.SetMinApprovedTimeNotSent(
                                moment(resp).toDate()
                            )
                    ),
                    catchError((error, caught) => {
                        if (error.status === 424) {
                            return fromShared.Errors.handleServiceError(error, caught).pipe(
                                concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                            );
                        }
                        return fromShared.Errors.handleServiceError(error, caught);
                    })
                )
            )
        )
    );
    getMaxApprovedTimeNotSent$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.GetMaxApprovedTimeNotSent>(
                actions.ActionTypes.GetMaxApprovedTimeNotSent
            ),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:GetMaxApprovedTimeNotSent$',
                    a
                })
            ),
            switchMap(() =>
                this.service.GetMaxApprovedTimeNotSent().pipe(
                    map(
                        resp =>
                            new actions.SetMaxApprovedTimeNotSent(
                                moment(resp).toDate()
                            )
                    ),
                    catchError((error, caught) => {
                        if (error.status === 424) {
                            return fromShared.Errors.handleServiceError(error, caught).pipe(
                                concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                            );
                        }
                        return fromShared.Errors.handleServiceError(error, caught);
                    })
                )
            )
        )
    );
    getContractorsWithHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.GetContractorsWithHours>(
                actions.ActionTypes.GetContractorsWithHours
            ),
            filter(a => !!a.payload.StartDate && !!a.payload.EndDate),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:getContractorsWithHours$',
                    a
                })
            ),
            switchMap(action =>
                this.service
                    .GetContractorsWithHours(
                        action.payload.StartDate,
                        action.payload.EndDate,
                        action.payload.Resend
                    )
                    .pipe(
                        map(resp => new actions.SetContractorsWithHours(resp)),
                        catchError((error, caught) => {
                            if (error.status === 424) {
                                return fromShared.Errors.handleServiceError(error, caught).pipe(
                                    concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                                );
                            }
                            return fromShared.Errors.handleServiceError(error, caught);
                        })
                    )
            )
        );
    });
    sendHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.SendHours>(actions.ActionTypes.SendHours),
            tap(a =>
                this.log.verbose({ type: 'QuickbooksEffects:sendHours$', a })
            ),
            concatMap(action =>
                this.service
                    .SendHours(
                        action.payload.EmployeeListID,
                        action.payload.startDate,
                        action.payload.endDate,
                        action.payload.resend
                    )
                    .pipe(
                        debounceTime(1000),
                        concatMap(resp => [
                            new actions.SendHoursSuccess(),
                            new actions.SetEmployeesWithHours([]),
                            new actions.GetEmployeesWithHours({
                                StartDate: action.payload.startDate,
                                EndDate: action.payload.endDate,
                                Resend: action.payload.resend
                            })
                        ]),
                        catchError((error, caught) => {
                            if (error.status === 424) {
                                return fromShared.Errors.handleServiceError(error, caught).pipe(
                                    concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                                );
                            }
                            return fromShared.Errors.handleServiceError(error, caught);
                        })
                    )
            )
        );
    });
    sendHoursSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType<actions.SendHoursSuccess>(
                    actions.ActionTypes.SendHoursSuccess
                ),
                tap(a =>
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendHoursSuccess$',
                        a
                    })
                ),
                map(() => {
                    this.messageService.clear();
                    setTimeout(() => {
                        this.messageService.add({
                            id: 'sendHoursSuccess',
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Hours sent successfully'
                        });
                    }, 0);
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendHoursSuccess$'
                    });
                })
            ),
        { dispatch: false }
    );

    sendBills$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.SendBills>(actions.ActionTypes.SendBills),
            tap(a =>
                this.log.verbose({ type: 'QuickbooksEffects:sendBills$', a })
            ),
            concatMap(action =>
                this.service
                    .SendHours(
                        action.payload.EmployeeListID,
                        action.payload.startDate,
                        action.payload.endDate,
                        action.payload.resend
                    )
                    .pipe(
                        debounceTime(1000),
                        concatMap(resp => [
                            new actions.SetContractorsWithHours([]),
                            new actions.GetContractorsWithHours({
                                StartDate: action.payload.startDate,
                                EndDate: action.payload.endDate,
                                Resend: action.payload.resend
                            })
                        ]),
                        catchError((error, caught) => {
                            if (error.status === 424) {
                                return fromShared.Errors.handleServiceError(error, caught).pipe(
                                    concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                                );
                            }
                            return fromShared.Errors.handleServiceError(error, caught);
                        })
                    )
            )
        );
    });
    sendBillsSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType<actions.SendBillsSuccess>(
                    actions.ActionTypes.SendBillsSuccess
                ),
                tap(a =>
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendBillsSuccess$',
                        a
                    })
                ),
                map(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Bills sent successfully'
                    });
                    // this.toaster.pop({
                    //     type: 'success',
                    //     title: 'Success',
                    //     body: 'Bills sent successfully',
                    //     sticky: true
                    // });
                })
            ),
        { dispatch: false }
    );
    sendJournalEntries$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.SendJournalEntries>(
                actions.ActionTypes.SendJournalEntries
            ),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:sendJournalEntries$',
                    a
                })
            ),
            concatMap(action =>
                this.service.SendJournalEntries(action.payload).pipe(
                    debounceTime(1000),
                    map(() => new actions.SendJournalEntriesSuccess()),
                    catchError((error, caught) => {
                        if (error.status === 424) {
                            return fromShared.Errors.handleServiceError(error, caught).pipe(
                                concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                            );
                        }
                        return fromShared.Errors.handleServiceError(error, caught);
                    })
                )
            )
        )
    );
    sendJournalEntriesSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType<actions.SendJournalEntriesSuccess>(
                    actions.ActionTypes.SendJournalEntriesSuccess
                ),
                tap(a =>
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendJournalEntriesSuccess$',
                        a
                    })
                ),
                map(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Journal Entries sent successfully'
                    });
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendJournalEntriesSuccess$'
                    });
                })
            ),
        { dispatch: false }
    );
    getEmployeesWithPurchases$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.GetEmployeesWithPurchases>(
                actions.ActionTypes.GetEmployeesWithPurchases
            ),
            filter(a => !!a.payload.StartDate && !!a.payload.EndDate),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:getEmployeesWithPurchases$',
                    a
                })
            ),
            switchMap(action =>
                this.service
                    .GetEmployeesWithPurchases(
                        action.payload.StartDate,
                        action.payload.EndDate,
                        action.payload.Resend
                    )
                    .pipe(
                        map(
                            resp => new actions.SetEmployeesWithPurchases(resp)
                        ),
                        catchError((error, caught) => {
                            if (error.status === 424) {
                                return fromShared.Errors.handleServiceError(error, caught).pipe(
                                    concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                                );
                            }
                            return fromShared.Errors.handleServiceError(error, caught);
                        })
                    )
            )
        );
    });
    sendPurchases$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.SendPurchases>(actions.ActionTypes.SendPurchases),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:SendPurchases$',
                    a
                })
            ),
            mergeMap(action =>
                this.service
                    .SendExpenses(
                        action.payload.EmployeeListID,
                        action.payload.startDate,
                        action.payload.endDate,
                        action.payload.Resend,
                        action.payload.sendImages
                    )
                    .pipe(
                        mergeMap(() => [
                            new actions.SetEmployeesWithPurchases([]),
                            new actions.SendPurchasesSuccess({
                                ...action.payload
                            })
                        ]),
                        catchError((error, caught) => {
                            if (error.status === 424) {
                                return fromShared.Errors.handleServiceError(error, caught).pipe(
                                    concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                                );
                            }
                            return fromShared.Errors.handleServiceError(error, caught);
                        })
                    )
            )
        );
    });
    updateEmployeesWithPurchases$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<actions.SendPurchasesSuccess>(
                actions.ActionTypes.SendPurchasesSuccess
            ),
            debounceTime(1000),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:updateEmployeesWithPurchases$',
                    a
                })
            ),
            map(
                action =>
                    // new actions.SetEmployeesWithPurchases([]),
                    new actions.GetEmployeesWithPurchases({
                        StartDate: action.payload.startDate,
                        EndDate: action.payload.endDate,
                        Resend: action.payload.Resend
                    })
            ),
            catchError((error, caught) => {
                if (error.status === 424) {
                    return fromShared.Errors.handleServiceError(error, caught).pipe(
                        concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                    );
                }
                return fromShared.Errors.handleServiceError(error, caught);
            })
        );
    });
    sendPurchasesSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType<actions.SendPurchasesSuccess>(
                    actions.ActionTypes.SendPurchasesSuccess
                ),
                debounceTime(1000),
                tap(a =>
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendPurchasesSuccess$',
                        a
                    })
                ),
                map(() => {
                    this.messageService.clear();
                    setTimeout(() => {
                        this.messageService.add({
                            id: 'sendPurchasesSuccess',
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Expenses sent successfully'
                        });
                    }, 0);
                    this.log.verbose({
                        type: 'QuickbooksEffects:sendPurchasesSuccess$'
                    });
                })
            ),
        { dispatch: false }
    );
    sendInvoices$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.SendInvoices>(actions.ActionTypes.SendInvoices),
            tap(a =>
                this.log.verbose({
                    type: 'QuickbooksEffects:SendInvoices$',
                    a
                })
            ),
            //TODO: how to combine this with switchmap so it doesn't stop on failure
            concatMap(action =>
                this.service.SendInvoices(action.payload).pipe(
                    tap(resp =>
                        this.log.verbose({
                            type: 'QuickbooksEffects:SendInvoices$',
                            action,
                            resp
                        })
                    ),
                    map(resp => ({
                        ...resp,
                        Items: resp.Items.map(item => {
                            return {
                                ...action.payload.find(
                                    x => x.id === item.Status.MsgEntityID
                                ),
                                ...item,
                                id: item.Status.MsgEntityID ?? item.id
                            };
                        })
                    })),
                    tap(resp =>
                        this.log.verbose({
                            type: 'QuickbooksEffects:SendInvoices$:2',
                            action,
                            resp
                        })
                    ),
                    concatMap(resp => {
                        const updated = this.actionFactory.create(
                            fromDataServices.Invoice.entityName,
                            EntityOp.QUERY_MANY_SUCCESS,
                            resp.Items
                        );
                        return fromShared.Errors.IsSuccessStatus(
                            resp.StatusCode
                        )
                            ? [
                                  new actions.SendInvoicesSuccess(resp.Items),
                                  new fromShared.Errors.RemoveErrors(),
                                  updated
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  ),
                                  updated
                              ];
                    }),
                    catchError((error, caught) => {
                        if (error.status === 424) {
                            return fromShared.Errors.handleServiceError(error, caught).pipe(
                                concatMap(errorAction => [new actions.HasValidTokens(false), errorAction])
                            );
                        }
                        return fromShared.Errors.handleServiceError(error, caught);
                    })
                )
            )
        )
    );

    revokeAccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType<actions.RevokeAccess>(actions.ActionTypes.RevokeAccess),
                tap(a => this.log.verbose({ type: 'revokeAccess$', a })),
                concatMap(action =>
                    this.service
                        .RevokeAccess()
                        .pipe(
                            debounceTime(1000),
                            catchError(fromShared.Errors.handleServiceError)
                        )
                )
            ),
        { dispatch: false }
    );
}
